import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {FormGroup, FormsModule} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {DatePipe, formatDate, NgForOf, NgIf, SlicePipe} from '@angular/common';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatTableDataSource} from "@angular/material/table";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {TimeSlotModel} from "../../../core/models/time-slot.model";
import {TimeSlotsModel} from "../../../core/models/time-slots.model";
import {FeedbackFormDeclinedComponent} from "../../components/feedback-form-declined/feedback-form-declined.component";
import {HeadlineTextComponent} from "../../../shared/components/headline-text/headline-text.component";
import {AppointmentService} from "../../services/appointment.service";
import {AppointmentModel} from "../../../core/models/appointment.model";
import {UrlTreeHelper} from "../../../core/services/url-tree-helper";
import {CommentModel} from "../../../core/models/comment.model";
import {HttpResponse} from "@angular/common/http";
import {TestRunService} from "../../services/test-run.service";
import {GtmEventDirective} from "../../../shared/directives/gtm-event.directive";
import {EmbedModeService} from "../../services/embed-mode.service";

@Component({
  selector: 'declined-timeslot-lookup',
  templateUrl: './declined-timeslot-lookup.component.html',
  styleUrls: ['./declined-timeslot-lookup.component.scss'],
  standalone: true,
  imports: [MatCard, NgIf, MatButton, MatIcon, FormsModule,
    DatePipe, TranslateModule, MatCardContent, MatRadioButton, FeedbackFormDeclinedComponent, NgForOf, HeadlineTextComponent,
    SlicePipe, GtmEventDirective, MatRadioGroup]
})

export class DeclinedTimeslotLookupComponent implements OnInit {

  public appointment!: AppointmentModel;
  public timeSlots!: TimeSlotsModel;
  public itemsToShow: number = 10;
  public locale: string;
  public showFeedbackForm: boolean = false;
  public dataSource: MatTableDataSource<TimeSlotModel>;
  selectedTimeSlot: TimeSlotModel | undefined | null;
  private isTestRun!: boolean;
  private comment!: CommentModel;
  private formValid: boolean = false;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslateService,
    private readonly appointmentService: AppointmentService,
    private readonly urlTreeHelper: UrlTreeHelper,
    private readonly testRunService: TestRunService,
    private readonly embedModeService: EmbedModeService) {

    this.appointment = this.route.snapshot.data['appointment'];
    this.timeSlots = this.route.snapshot.data['timeSlots'];
    this.locale = this.translateService.currentLang;
    this.isTestRun = this.testRunService.isTestRun();

    this.dataSource = new MatTableDataSource<TimeSlotModel>(this.timeSlots.items);
  }

  ngOnInit() {
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.locale = this.translateService.currentLang;
    });

    if (this.timeSlots) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'alternative_dates_found',
        'totalNumberOfAppointments': this.timeSlots.items.length || 0,
      });
    }
  }

  loadMore() {
    setTimeout(() => {
      this.itemsToShow += 10;
    }, 500);
  }

  handleFeedbackForm(feedbackForm: FormGroup): void {
    this.formValid = feedbackForm.valid;
  }

  receiveOutput(comment: CommentModel): void {
    this.comment = comment;
  }

  disableSendButton(): boolean {
    return !this.selectedTimeSlot && !this.formValid;
  }

  onTimeSlotSelect(timeSlot: TimeSlotModel | null): void {
    this.selectedTimeSlot = timeSlot;
    this.showFeedbackForm = !timeSlot;
  }

  getAriaLabelForTimeSlot(timeSlot: TimeSlotModel): string {
    if (this.timeSlots.displayType === 'FROM_TILL') {
      return this.translateService.instant('APPOI.LABEL_ARRIVAL_TECHNICIAN.DISPLAY_TYPE_FROM_TILL.ARIA',
        {
          beginDate: formatDate(timeSlot.displayBegin, 'mediumDate', this.locale, this.appointment.timezone),
          beginTimeFrom: formatDate(timeSlot.displayBegin, 'shortTime', this.locale, this.appointment.timezone),
          beginTimeEnd: formatDate(timeSlot.displayEnd, 'shortTime', this.locale, this.appointment.timezone)
        });
    } else {
      return this.translateService.instant('APPOI.LABEL_ARRIVAL_TECHNICIAN.DISPLAY_TYPE_FROM.ARIA',
        {
          beginDate: formatDate(timeSlot.displayBegin, 'mediumDate', this.locale, this.appointment.timezone),
          beginTimeFrom: formatDate(timeSlot.displayBegin, 'shortTime', this.locale, this.appointment.timezone)
        });
    }
  }

  sendData(clicked: boolean): void {
    if (clicked) {
      this.send();
    }
  }

  send() {
    const BASE_PATH = '/' + this.route.snapshot.params['factoryId'] + '/' + this.route.snapshot.params['language'] + '/' + this.route.snapshot.params['id'];
    if (this.selectedTimeSlot) {
      // book time slot
      this.appointmentService
        .bookTimeSlot(this.appointment.id, this.selectedTimeSlot, this.isTestRun)
        .subscribe({
          next: data => {
            this.embedModeService.reloadDataEvent();
            this.router.navigateByUrl(this.urlTreeHelper.generateUrlTree(
              BASE_PATH, {suffix: 'proposal/alternative-timeslot-accepted'}));
          },
          error: error => {
            this.router.navigateByUrl(this.urlTreeHelper.generateUrlTree(
              BASE_PATH, {suffix: 'proposal/failed'}));
          }
        });
    } else {
      this.appointmentService
        .addComment(this.appointment, this.comment, this.isTestRun)
        .subscribe((data: HttpResponse<any>) => {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            'event': 'add_note',
            'flowType': 'PROPOSAL_DECLINED',
            'contactReason': this.comment.typeOfRequestKey,
          });
          this.router.navigateByUrl(this.urlTreeHelper.generateUrlTree(
            BASE_PATH, {suffix: 'message-received'}), {state: this.comment});
        });
    }
  }

}
